import { useEffect, useMemo, useRef, useState } from "react";
import {
  Renderer, Stave, StaveNote, StaveConnector, Voice, Formatter,
  Accidental, Beam, Dot, StaveTie, Articulation, Barline,
} from "vexflow";
import type { Hand, Score, ScoreNote } from "./types";
import { beamsForBar } from "./beaming";
import { playheadPosition } from "./playhead";

/**
 * The engraved score, and the surface the student actually reads.
 *
 * Carried over from the Rhythm Arithmetic notation engine, because that one
 * reads like real engraving rather than like software:
 *
 *  - Beams are GENERATED from where each note falls in the bar, never
 *    hand-built. See `beaming.ts` for the rules.
 *  - Ties are honoured across barlines and across system breaks.
 *  - The playhead recolours individual noteheads rather than sliding a bar over
 *    the top, so the student's eye lands on the note that is sounding.
 *  - Bars are laid out into systems that fill the available width, and the whole
 *    thing re-engraves on resize, so it works on a laptop and on an iPad stand.
 *
 * VexFlow 4 traps this code deliberately avoids (all three cost real debugging
 * in this repo before):
 *
 *  - StaveNote does NOT understand "qd" for a dotted quarter. Passing it draws
 *    no dot AND counts the wrong ticks, so bars silently come out short. Dots
 *    must be stripped from the duration and reattached with Dot.buildAndAttach.
 *
 *  - A StaveNote spelled "g#/4" draws a G with NO SHARP unless an Accidental
 *    modifier is attached. `Accidental.applyAccidentals` attaches them, per bar,
 *    the way MuseScore does — and it MUST be called for every key including C.
 *    Skipping it in C major is what printed Rock 'n' Roll's ninety-odd flats and
 *    naturals as plain white notes, and printed A Minor Waltz's G sharp — the
 *    one accidental the piece exists to teach — as a G natural.
 *
 *  - `Beam.generateBeams` re-derives the beat grid by counting ticks from the
 *    first note and loses it the moment a note straddles a group boundary. Beams
 *    come from `beamsForBar` instead, which reads each note's absolute position.
 */

const VEX_DUR: Record<string, string> = {
  whole: "w", half: "h", quarter: "q", eighth: "8", "16th": "16", "32nd": "32",
};

const SHARP_KEYS = ["C", "G", "D", "A", "E", "B", "F#", "C#"];
const FLAT_KEYS = ["C", "F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb"];

function keySignatureName(fifths: number): string {
  return fifths >= 0 ? SHARP_KEYS[Math.min(fifths, 7)] : FLAT_KEYS[Math.min(-fifths, 7)];
}

/** "F#4" -> "f#/4" as VexFlow spells keys. */
function toVexKey(pitch: string): string {
  const m = pitch.replace(/♯/g, "#").replace(/♭/g, "b")
    .match(/^([A-G])(#{1,2}|b{1,2})?(-?\d+)$/);
  if (!m) {
    // Silently drawing middle C for an unparseable pitch is how a transcription
    // error hides. Say so, loudly, in development.
    if (import.meta.env?.DEV) console.error(`[ScoreView] cannot spell pitch "${pitch}"`);
    return "c/4";
  }
  const [, letter, acc = "", oct] = m;
  return `${letter.toLowerCase()}${acc}/${oct}`;
}

interface BarGroup {
  bar: number;
  /** Notes per hand, in sounding order, chord members adjacent. */
  byHand: Record<Hand, ScoreNote[]>;
  /** Absolute quarter-note position of this bar's downbeat. */
  start: number;
}

function groupIntoBars(score: Score): BarGroup[] {
  const map = new Map<number, BarGroup>();
  for (const n of score.notes) {
    let g = map.get(n.bar);
    if (!g) {
      g = { bar: n.bar, byHand: { rh: [], lh: [] }, start: Infinity };
      map.set(n.bar, g);
    }
    g.byHand[n.hand].push(n);
    if (n.start < g.start) g.start = n.start;
  }
  for (const g of map.values()) {
    // The printed downbeat, not merely the first thing that sounds — a hand can
    // enter late without a written rest.
    g.start = Math.min(g.start, (g.bar - 1) * score.barQuarters);
  }
  return [...map.values()].sort((a, b) => a.bar - b.bar);
}

/** Collapse chord members into one StaveNote per onset. */
function toVoiceEntries(notes: ScoreNote[]): ScoreNote[][] {
  const out: ScoreNote[][] = [];
  for (const n of notes) {
    if (n.chord && out.length) out[out.length - 1].push(n);
    else out.push([n]);
  }
  return out;
}

interface NoteSlot {
  x: number;
  sys: number;
  /** The drawn <g>, so the playhead can recolour it without a re-engrave. */
  el: SVGElement | null;
  dim: boolean;
}

export interface ScoreViewProps {
  score: Score;
  /** Index into `score.notes` of the note currently sounding, or null. */
  playingIndex?: number | null;
  /** Dim the hand that is switched off rather than hiding the staff. */
  focusHand?: Hand | "both";
  /** Bars to show; omit for the whole piece. Used by loop practice. */
  barRange?: [number, number];
  /** Show the lyric line under the melody. */
  showLyrics?: boolean;
  /** Bars per system. Auto-fits to width when omitted. */
  barsPerLine?: number;
  /**
   * Absolute position of the playhead in quarter notes from the top of the
   * piece, or null when stopped. Deliberately NOT a dependency of the engrave
   * effect: it changes every animation frame, and re-engraving 60 times a
   * second would melt the page. The bar is a cheap overlay read from the
   * layout the last engrave recorded.
   */
  playheadQ?: number | null;
  /** Live playhead position, read once per frame. Preferred over playheadQ:
   *  it moves the bar without re-rendering the score. */
  getPlayheadQ?: () => number | null;
  className?: string;
}

export function ScoreView({
  score, playingIndex = null, focusHand = "both",
  barRange, showLyrics = true, barsPerLine, className = "",
  playheadQ = null,
  getPlayheadQ,
}: ScoreViewProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  /** Where each note landed, filled by the engrave pass, read by the playhead. */
  const layoutRef = useRef<{
    notes: Map<number, NoteSlot>;
    systems: { y: number; h: number; xEnd: number }[];
  }>({ notes: new Map(), systems: [] });
  const [layoutTick, setLayoutTick] = useState(0);

  // Re-engrave when the container changes size. A score that only lays out once
  // is unreadable on the device it was not measured on.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const ro = new ResizeObserver(() => {
      const w = host.getBoundingClientRect().width;
      if (w > 80) setWidth(Math.floor(w));
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  const bars = useMemo(() => {
    const all = groupIntoBars(score);
    if (!barRange) return all;
    return all.filter((b) => b.bar >= barRange[0] && b.bar <= barRange[1]);
  }, [score, barRange]);

  /** Absolute index of each note within score.notes, for playhead matching. */
  const indexOf = useMemo(() => {
    const m = new Map<ScoreNote, number>();
    score.notes.forEach((n, i) => m.set(n, i));
    return m;
  }, [score]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !bars.length) return;
    host.innerHTML = "";

    const timeSig = `${score.time[0]}/${score.time[1]}`;
    const grand = score.staves > 1;
    const keySig = keySignatureName(score.fifths);

    // Layout. Bar 1 carries clef + key + time, so it needs a wider slot.
    const CLEF_KEY = 46 + Math.abs(score.fifths) * 11;
    const TIME_SIG = 26;
    const MARGIN = 10;
    // A sung bar has to hold a word under every note, so it needs more room
    // than an instrumental one. Fitting both at the same 168px was what made
    // syllables collide on the busier songs and on narrow screens.
    const minBarPx = showLyrics && score.hasLyrics ? 208 : 168;
    const perLine = barsPerLine
      ?? Math.max(1, Math.min(bars.length,
           Math.floor((width - CLEF_KEY - TIME_SIG) / minBarPx) || 1));
    const lines: BarGroup[][] = [];
    for (let i = 0; i < bars.length; i += perLine) lines.push(bars.slice(i, i + perLine));

    const staffGap = 92;
    const lineHeight = grand ? staffGap + 108 : (showLyrics && score.hasLyrics ? 132 : 104);
    const height = lines.length * lineHeight + 34;

    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();

    // A tie can span a barline and even a system break. VexFlow cannot draw one
    // StaveTie between two systems — given notes on different lines it joins them
    // with a straight diagonal across the page. So a crossing tie is drawn as two
    // fragments: one trailing off the end of the first system, one arriving at the
    // start of the next. `line` remembers which system a note was drawn on.
    const pendingTie: Partial<Record<Hand, { note: StaveNote; line: number }>> = {};
    const ties: StaveTie[] = [];
    const beams: Beam[] = [];

    const layout = {
      notes: new Map<number, NoteSlot>(),
      systems: [] as { y: number; h: number; xEnd: number }[],
    };

    lines.forEach((lineBars, li) => {
      const y = 18 + li * lineHeight;
      /** Syllables for this whole system, placed only once every bar is laid
       *  out — a per-bar pass cannot see the collision across a barline. */
      const lyricRow: { x: number; text: string }[] = [];
      /** Set from the stave itself — a baseline guessed from the system top
       *  lands on the notes instead of under them. */
      let lyricBaseline = 0;
      layout.systems[li] = { y, h: grand ? staffGap + 48 : 48, xEnd: width - MARGIN };
      const first = li === 0;
      const usable = width - MARGIN * 2;
      const prefixHere = CLEF_KEY + (first ? TIME_SIG : 0);
      // A final short system should not stretch its bars across the full width —
      // that is the tell-tale sign of software rather than engraving. Give it the
      // same bar width the full systems use.
      const fullBarW = Math.floor((usable - CLEF_KEY - TIME_SIG) / perLine);
      const barW = lineBars.length < perLine
        ? fullBarW
        : Math.floor((usable - prefixHere) / lineBars.length);
      let x = MARGIN;
      let lineTopStave: Stave | null = null;
      let lineBotStave: Stave | null = null;

      lineBars.forEach((bg, bi) => {
        const w = bi === 0 ? barW + prefixHere : barW;
        const staveTop = new Stave(x, y, w);
        const staveBot = grand ? new Stave(x, y + staffGap, w) : null;

        // A piece ends on a final double barline — thin, then thick. Every bar
        // here is its own Stave, and VexFlow gives a Stave a plain single
        // barline unless told otherwise, so the last bar of the piece was
        // ending exactly like bar 4 of 24: the music simply stopped. In print
        // that mark is how a reader knows the piece is over.
        if (li === lines.length - 1 && bi === lineBars.length - 1) {
          staveTop.setEndBarType(Barline.type.END);
          staveBot?.setEndBarType(Barline.type.END);
        }

        if (bi === 0) {
          // Clef and key signature repeat on every system, as in any printed
          // part. The time signature appears ONCE, at the top of the piece —
          // repeating it on each system is what an engraver would mark as wrong.
          staveTop.addClef(score.clefs[0] ?? "treble");
          if (score.fifths !== 0) staveTop.addKeySignature(keySig);
          if (first) staveTop.addTimeSignature(timeSig);
          if (staveBot) {
            staveBot.addClef(score.clefs[1] ?? "bass");
            if (score.fifths !== 0) staveBot.addKeySignature(keySig);
            if (first) staveBot.addTimeSignature(timeSig);
          }
          lineTopStave = staveTop;
          lineBotStave = staveBot;
        }
        staveTop.setContext(ctx).draw();
        staveBot?.setContext(ctx).draw();

        const handsHere: Hand[] = grand ? ["rh", "lh"] : ["rh"];
        handsHere.forEach((hand, hi) => {
          const stave = hi === 0 ? staveTop : (staveBot as Stave);
          const clef = (score.clefs[hi] ?? (hi === 0 ? "treble" : "bass")) as "treble" | "bass";
          const entries = toVoiceEntries(bg.byHand[hand]);
          const dimmed = focusHand !== "both" && focusHand !== hand;

          if (!entries.length) {
            // A silent bar in a grand staff still has to say so. Left empty, the
            // staff reads as a printing fault rather than as a bar's rest. A
            // semibreve rest means "the whole bar" in any metre.
            if (!grand) return;
            const bar = new StaveNote({ keys: [clef === "bass" ? "d/3" : "b/4"],
                                        duration: "wr", clef });
            if (dimmed) bar.setStyle({ fillStyle: DIM, strokeStyle: DIM });
            const v = new Voice({ num_beats: score.time[0], beat_value: score.time[1] })
              .setStrict(false);
            v.addTickables([bar]);
            new Formatter().joinVoices([v]).format([v], w - (bi === 0 ? prefixHere + 16 : 20));
            v.draw(ctx, stave);
            return;
          }

          const vexNotes = entries.map((group) => {
            const head = group[0];
            const base = VEX_DUR[head.d] ?? "q";
            const isRest = !!head.rest;
            const dots = head.dots ?? (head.dot ? 1 : 0);
            const note = new StaveNote({
              keys: isRest
                ? [clef === "bass" ? "d/3" : "b/4"]
                : group.map((g) => toVexKey(g.pitch as string)),
              duration: isRest ? `${base}r` : base,
              // The dot count makes the note LAST its dotted length, which is
              // what the formatter spaces the bar by; Dot.buildAndAttach below
              // only DRAWS the dot. Without this a dotted minim took a minim's room.
              dots,
              clef,
              // VexFlow does NOT work out stem direction on its own: without
              // auto_stem every stem points UP, so anything above the middle
              // line was engraved upside down and collided with the lyric
              // line above it. A stem's direction is not decoration — it is
              // how a reader's eye finds the shape of a phrase.
              auto_stem: true,
            });
            for (let d = 0; d < dots; d++) Dot.buildAndAttach([note], { all: true });

            if (head.grace) note.setStyle({ fillStyle: "#555", strokeStyle: "#555" });
            else if (dimmed) note.setStyle({ fillStyle: DIM, strokeStyle: DIM });

            if (head.art?.length) {
              for (const a of head.art) {
                // A real articulation glyph. The old code drew a typed full stop
                // through Annotation, which is a text character sitting under the
                // note, not a staccato dot — and on Meet the Flintstones, whose
                // own heading says "watch out for the Staccato notes", that is
                // the first thing a reader looks for. Placement is left to
                // VexFlow, which puts the mark on the side away from the stem.
                if (a === "staccato") note.addModifier(new Articulation("a."), 0);
                else if (a === "accent") note.addModifier(new Articulation("a>"), 0);
                else if (a === "tenuto") note.addModifier(new Articulation("a-"), 0);
                else if (a === "marcato") note.addModifier(new Articulation("a^"), 0);
              }
            }

            return { note, head, group };
          });

          const voice = new Voice({
            num_beats: score.time[0], beat_value: score.time[1],
          }).setStrict(false);
          voice.addTickables(vexNotes.map((v) => v.note));

          // Accidentals, ALWAYS — including in C major and A minor, where every
          // printed sharp and flat is by definition outside the key signature.
          try {
            (Accidental as unknown as {
              applyAccidentals: (v: Voice[], k: string) => void;
            }).applyAccidentals([voice], keySig);
          } catch {
            /* leave notes unforced rather than failing the whole render */
          }

          // Beams from the real beat grid, not from a tick count.
          try {
            beams.push(...beamsForBar(vexNotes, {
              barStart: bg.start,
              barQuarters: score.barQuarters,
              time: score.time,
            }));
          } catch {
            /* an odd bar simply goes unbeamed rather than breaking the page */
          }

          new Formatter().joinVoices([voice]).format(
            [voice], w - (bi === 0 ? prefixHere + 16 : 20));
          voice.draw(ctx, stave);

          // Remember where every notehead landed, and the element it was drawn
          // into, so the playhead can sweep and recolour without a re-engrave.
          for (const vn of vexNotes) {
            let el: SVGElement | null = null;
            try { el = vn.note.getSVGElement() as SVGElement ?? null; } catch { el = null; }
            // Colour is carried by a CLASS, not by an inline fill. The page's
            // ink rule names the <path> elements directly, so an inherited
            // colour on this <g> — however it is set — never reaches them.
            if (el && dimmed) el.classList.add("sr-dim");
            const at = { x: vn.note.getAbsoluteX(), sys: li, el, dim: dimmed };
            for (const member of vn.group) {
              const a = indexOf.get(member);
              if (a != null && !layout.notes.has(a)) layout.notes.set(a, at);
            }
          }

          // Lyrics sit on ONE baseline for the whole system. Hanging them off
          // each notehead as an annotation makes the line visibly ragged,
          // because a low note pushes its own syllable further down.
          if (showLyrics && hand === "rh") {
            lyricBaseline = stave.getYForLine(4) + 34;
            for (const { note, head } of vexNotes) {
              if (head.lyric) lyricRow.push({ x: note.getAbsoluteX(), text: head.lyric });
            }
          }

          // Ties, including across barlines and system breaks.
          vexNotes.forEach(({ note, head }) => {
            const open = pendingTie[hand];
            if (head.tie === "stop" || head.tie === "both") {
              if (open) {
                if (open.line === li) {
                  ties.push(new StaveTie({ first_note: open.note, last_note: note }));
                } else {
                  // Two fragments rather than one diagonal across the page.
                  ties.push(new StaveTie({
                    first_note: open.note, last_note: null,
                    first_indices: [0], last_indices: [0],
                  }));
                  ties.push(new StaveTie({
                    first_note: null, last_note: note,
                    first_indices: [0], last_indices: [0],
                  }));
                }
              }
              pendingTie[hand] = undefined;
            }
            if (head.tie === "start" || head.tie === "both") {
              pendingTie[hand] = { note, line: li };
            }
          });
        });

        x += w;
      });

      // Brace and the left/right barlines that make a grand staff read as one.
      if (grand && lineTopStave && lineBotStave) {
        new StaveConnector(lineTopStave, lineBotStave)
          .setType(StaveConnector.type.BRACE).setContext(ctx).draw();
        new StaveConnector(lineTopStave, lineBotStave)
          .setType(StaveConnector.type.SINGLE_LEFT).setContext(ctx).draw();
      }

      // ---- lyrics, fitted ----
      // Centre each syllable on its note, but never on top of its neighbour:
      // shrink the whole line until it can fit, then walk left to right and
      // push any syllable that would collide. "Flint stones" printing as
      // "Flintstones" is the defect this removes.
      if (lyricRow.length) {
        const measure = (t: string) =>
          (ctx as unknown as { measureText: (s: string) => { width: number } })
            .measureText(t).width;
        const GAP = 5;
        const avail = width - MARGIN * 2;
        let size = 12;
        let widths: number[] = [];
        for (;;) {
          ctx.setFont("ui-sans-serif", size, "");
          widths = lyricRow.map((l) => measure(l.text));
          const need = widths.reduce((a, b) => a + b, 0) + GAP * (lyricRow.length - 1);
          if (need <= avail || size <= 8) break;
          size -= 0.5;
        }
        ctx.save();
        ctx.setFont("ui-sans-serif", size, "");
        ctx.setFillStyle(INK);
        let cursor = -Infinity;
        const baseline = lyricBaseline || y + 74;
        lyricRow.forEach((l, i) => {
          let lx = l.x - widths[i] / 2;
          if (lx < cursor) lx = cursor;          // never overlap the previous word
          if (lx + widths[i] > width - MARGIN)   // never run off the system
            lx = Math.max(cursor, width - MARGIN - widths[i]);
          ctx.fillText(l.text, lx, baseline);
          cursor = lx + widths[i] + GAP;
        });
        ctx.restore();
      }

      // Bar number at the start of each system, the way a printed part does it.
      const startBar = lineBars[0].bar;
      ctx.save();
      ctx.setFont("ui-sans-serif", 10, "600");
      ctx.setFillStyle(MUTED);
      ctx.fillText(String(startBar), MARGIN + 2, y - 4);
      ctx.restore();
    });

    beams.forEach((b) => b.setContext(ctx).draw());
    ties.forEach((t) => t.setContext(ctx).draw());

    layoutRef.current = layout;
    setLayoutTick((t) => t + 1);
  }, [bars, width, score, focusHand, showLyrics, barsPerLine, indexOf]);

  // Recolour the sounding notehead in place. This used to be a dependency of the
  // engrave effect, which meant the whole piece was torn down and re-laid-out on
  // every single note — several times a second on Rock 'n' Roll, and visible as
  // a flicker. Touching one element's fill costs nothing.
  const paintedRef = useRef<number | null>(null);
  useEffect(() => {
    const { notes } = layoutRef.current;
    const prev = paintedRef.current;
    if (prev != null && prev !== playingIndex) {
      notes.get(prev)?.el?.classList.remove("sr-playing");
    }
    if (playingIndex != null) {
      notes.get(playingIndex)?.el?.classList.add("sr-playing");
    }
    paintedRef.current = playingIndex;
  }, [playingIndex, layoutTick]);

  // Where the bar sits right now. Interpolates between the two notes either
  // side of the clock so the sweep is continuous rather than hopping notehead
  // to notehead, and clamps to the end of a system when the music wraps to the
  // next line (a bar cannot travel diagonally across a page).
  // ── PLAYHEAD ────────────────────────────────────────────────────────────
  // Positioned every animation frame by moving ONE element with a transform.
  // It used to be a React element re-rendered from state sixty times a second,
  // animating `left` with a 60 ms CSS transition: the whole score re-rendered
  // per frame, `left` forced a layout each time, and the transition meant that
  // when the music wrapped to the next line the bar visibly slid backwards
  // across the page instead of appearing where the music now is.
  const headRef = useRef<HTMLDivElement | null>(null);
  const soundingRef = useRef<{ start: number; dur: number; i: number }[]>([]);

  useEffect(() => {
    soundingRef.current = score.notes
      .map((n, i) => ({ n, i }))
      .filter(({ n, i }) => !n.rest && layoutRef.current.notes.has(i))
      .sort((a, b) => a.n.start - b.n.start)
      .map(({ n, i }) => ({ start: n.start, dur: n.dur, i }));
  }, [score, layoutTick]);

  useEffect(() => {
    const read = getPlayheadQ ?? (() => playheadQ);
    let raf = 0;
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const el = headRef.current;
      if (!el) return;
      const q = read();
      const p = playheadPosition(q, soundingRef.current,
                                 layoutRef.current.notes as unknown as Map<number, { x: number; sys: number }>,
                                 layoutRef.current.systems);
      if (!p) {
        if (el.style.opacity !== "0") el.style.opacity = "0";
        return;
      }
      el.style.opacity = "1";
      el.style.height = `${p.h}px`;
      el.style.transform = `translate3d(${p.x - 1}px, ${p.y}px, 0)`;
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [getPlayheadQ, playheadQ, layoutTick, score]);

  return (
    <div className={`overflow-x-auto ${className}`}>
      {/* Relative only — sizing to content would let the stave grow past a
          phone's viewport and clip the last syllable of every line. */}
      <div className="relative">
        <div ref={hostRef} className="sr-score min-w-[260px]" />
        <div
          ref={headRef}
          aria-hidden
          className="pointer-events-none absolute left-0 top-0"
          style={{
            width: 2, opacity: 0,
            background: PLAYHEAD,
            boxShadow: `0 0 0 1px ${PLAYHEAD}33, 0 0 12px ${PLAYHEAD}66`,
            borderRadius: 1,
            willChange: "transform",
          }}
        />
      </div>
    </div>
  );
}

/** Playhead colour. Matches the acid accent the Rhythm Arithmetic app uses. */
const PLAYHEAD = "#c8f000";
const DIM = "#b8b8c0";
const MUTED = "#8a8a99";
const INK = "#15151d";
